import { ContentCopy as ContentCopyIcon, JoinFull as JoinFullIcon, SplitIcon } from 'components/icons';
import StatusChip from 'components/@extended/StatusChip';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { BoxNowrap } from 'components/misc/BoxNoWrap.tsx';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import ListClamp from 'components/misc/ListClamp.tsx';
import SupervisedAndResponsibleEmployees from 'components/misc/SupervisedAndResponsibleEmployees.tsx';
import PartyName from 'components/party/PartyName';
import PdfLink from 'components/pdflink/PdfLink';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Checkbox } from 'components/ui/checkbox';
import { Tooltip } from 'components/ui/tooltip';
import Unmasked from 'components/Unmasked';
import HoursDisplay from 'components/util/HoursDisplay';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { getLinks } from 'pages/matters/util';
import React, { HTMLAttributeAnchorTarget } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/reducers';
import { toggleBulkSendInvoice, toggleDebtReminderInvoice } from 'store/reducers/invoices';
import { ClassifierData } from 'types/classifiers';
import {
    InvoiceAmountDetailsTotalsDto,
    InvoiceDataListItem,
    InvoiceListItemTotalsDto,
    InvoiceType,
} from 'types/invoices';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { formatShareRate } from './payers/paymentShare';
import {
    getInvoiceStatusChipType,
    getInvoiceTypeLetter,
    invoicePath,
    isDebtReminderEligible,
} from './util/invoiceUtil';

// `entity-link` hands the colour and wrapping to the table's own rule inside a list cell.
const ENTITY_LINK = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

/** Accepted and ignored: colours come from the tokens. */
type IgnoredTheme = { theme?: unknown };

// The marks below carry their tooltip text as an accessible name too, so a screen reader gets it without a hover.

export const DebtReminderCheckbox = ({ invoice }: { invoice: InvoiceDataListItem }) => {
    const { id } = invoice;
    const dispatch = useDispatch();
    const selectedDebtReminderInvoiceIds = useSelector(
        (state: RootState) => state.invoices.selectedDebtReminderInvoiceIds
    );

    if (!isDebtReminderEligible(invoice)) {
        return null;
    }

    return (
        <Checkbox
            checked={selectedDebtReminderInvoiceIds.includes(id)}
            onCheckedChange={() => dispatch(toggleDebtReminderInvoice(invoice))}
        />
    );
};

export const BulkSendCheckbox = React.memo(({ invoiceId }: { invoiceId: number }) => {
    const dispatch = useDispatch();
    const isSelected = useSelector((state: RootState) => state.invoices.selectedBulkSendInvoiceIds.includes(invoiceId));

    return <Checkbox checked={isSelected} onCheckedChange={() => dispatch(toggleBulkSendInvoice(invoiceId))} />;
});

export const SplitInvoiceIcon = ({ className }: IgnoredTheme & { className?: string }) => (
    <Tooltip content={<FormattedMessage id={'invoices.split'} />} className="w-fit text-center">
        <div role="img" aria-label={t('invoices.split')} className={className}>
            <ContentCopyIcon fontSize="small" className="text-primary" />
        </div>
    </Tooltip>
);

const NO_SHARE = '—';

/** The share a shared-payment copy pays, naming the other copies of its group on hover; a dash otherwise. */
export const InvoicePaymentShare = ({ invoice }: { invoice: InvoiceDataListItem }) => {
    if (invoice.paymentShareRate === null || invoice.paymentShareRate === undefined) {
        return <span className="text-body1 text-fg-secondary">{NO_SHARE}</span>;
    }
    const label = `${formatShareRate(invoice.paymentShareRate)}%`;
    const otherInvoiceNumbers = invoice.sharedPaymentInvoiceNumbers ?? [];
    const chip = (
        <StatusChip type="info" icon={<SplitIcon className="size-3" />} label={label} className="gap-1 px-1.5" />
    );
    if (otherInvoiceNumbers.length === 0) {
        return chip;
    }
    const tooltip = t('invoices.payers.list-tooltip', { numbers: otherInvoiceNumbers.join(', ') });
    return (
        <Tooltip content={tooltip} className="w-fit text-center">
            <div role="img" aria-label={`${label}. ${tooltip}`} className="inline-flex w-fit">
                {chip}
            </div>
        </Tooltip>
    );
};

export const InvoiceTypeChip = ({
    typeCode,
    typeLabel,
}: IgnoredTheme & {
    typeCode: string;
    typeLabel: string;
}) => (
    <div className="text-fg-secondary new:pr-0 pr-4">
        <Tooltip content={typeLabel} className="w-fit text-center">
            <div role="img" aria-label={typeLabel} className="w-fit">
                <StatusChip label={getInvoiceTypeLetter(typeCode)} type="neutral" />
            </div>
        </Tooltip>
    </div>
);

const JointInvoiceIcon = ({ className }: { className?: string }) => (
    <Tooltip content={<FormattedMessage id={'invoices.joint-invoice'} />} className="w-fit text-center">
        <div role="img" aria-label={t('invoices.joint-invoice')} className={className}>
            <JoinFullIcon fontSize="small" className="text-primary" />
        </div>
    </Tooltip>
);

/**
 * The type chip and the joint / split icons: a column leading the cell, or in the new design a row on the cell's
 * bottom line (`row`), where a list cell keeps its chips.
 */
export const InvoiceIconGroup = ({
    typeCode,
    typeLabel,
    isJointInvoice,
    isSplit,
    row = false,
}: IgnoredTheme & {
    typeCode: string;
    typeLabel: string;
    isJointInvoice: boolean;
    isSplit: boolean;
    row?: boolean;
}) => (
    <div className={cn('flex cursor-default flex-col gap-1', row && 'flex-row items-center')}>
        <InvoiceTypeChip typeCode={typeCode} typeLabel={typeLabel} />
        {isJointInvoice && <JointInvoiceIcon className="pl-0.5" />}
        {isSplit && <SplitInvoiceIcon className="pl-0.5" />}
    </div>
);

export const InvoiceNumberClientPayer = ({
    invoice,
    classifiers,
    contactId,
    matterId,
    hideIcons = false,
}: IgnoredTheme & {
    invoice: InvoiceDataListItem;
    classifiers: Record<string, ClassifierData>;
    contactId?: string;
    matterId?: string;
    /** Drop the type / joint / split icons, for views that already render them in another column. */
    hideIcons?: boolean;
}): JSX.Element => {
    const isNewDesign = useIsNewDesign();
    const { id, isSplit, matters, invoiceNumber, clientParty, invoiceTypeCode, invoiceMattersClientParties } = invoice;
    const invoiceTypeCodeClassifierValue = getClassifierText(classifiers['INVOICE_TYPE'], invoiceTypeCode);
    const hasDifferingInvoiceClient =
        invoiceMattersClientParties.length > 1 ||
        (invoiceMattersClientParties.length === 1 && invoiceMattersClientParties.at(0)?.id !== clientParty.id);
    const clientLinks = getLinks('contact', invoiceMattersClientParties, {
        showPartyNumber: true,
        useLinkWithOrigin: true,
        partyVariant: 'list',
    });
    const icons = !hideIcons && (
        <InvoiceIconGroup
            typeCode={invoiceTypeCode}
            typeLabel={invoiceTypeCodeClassifierValue}
            isJointInvoice={matters.length > 1}
            isSplit={isSplit ?? false}
            row={isNewDesign}
        />
    );
    return (
        <div className={isNewDesign ? 'flex flex-col gap-1' : 'flex flex-row'}>
            {isNewDesign ? null : icons}
            <div className="flex flex-col">
                <LinkWithOrigin
                    className={cn(ENTITY_LINK, 'font-semibold whitespace-normal')}
                    to={invoicePath(id, { contactId, matterId })}
                >
                    <Unmasked>{invoiceNumber}</Unmasked>
                </LinkWithOrigin>
                <div className="text-body1">{clientLinks}</div>
                {hasDifferingInvoiceClient && (
                    <div className="text-body1">
                        <FormattedMessage
                            id="invoices.payer"
                            values={{
                                client: (
                                    <PartyName
                                        key="client"
                                        party={clientParty}
                                        isLink
                                        useLinkWithOrigin
                                        variant="list"
                                        showPartyNumber
                                    />
                                ),
                            }}
                        />
                    </div>
                )}
            </div>
            {isNewDesign ? icons : null}
        </div>
    );
};

export const InvoiceMatter = ({
    invoice,
    fullName = false,
    className,
}: {
    invoice: InvoiceDataListItem;
    fullName?: boolean;
    className?: string;
}): JSX.Element => {
    const { matters } = invoice;
    const sortedMatters = matters.toSorted((a, b) => b.id - a.id);
    return (
        <ListClamp
            items={sortedMatters}
            maxVisibleItems={1}
            getKey={({ id }) => id}
            renderItem={(matter) => (
                <RecordLink
                    className={ENTITY_LINK}
                    to={`/matter/${matter.id}`}
                    label={fullName ? `${matter.caseNumber} ${matter.name}` : matter.caseNumber}
                >
                    <BoxNowrap>{matter.caseNumber}</BoxNowrap>
                    {/* A span so the name keeps flowing onto its own line after the block above,
                        greyed like the secondary line of the money columns. Still inside the link,
                        so the whole cell stays one click target. */}
                    {fullName && <span className="text-body1 text-fg-secondary">{matter.name}</span>}
                </RecordLink>
            )}
            className={className}
        />
    );
};

export const InvoiceClientPayer = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    const { clientParty, invoiceMattersClientParties } = invoice;
    const hasDifferingInvoiceClient =
        invoiceMattersClientParties.length > 1 ||
        (invoiceMattersClientParties.length === 1 && invoiceMattersClientParties.at(0)?.id !== clientParty.id);
    const clientLinks = getLinks('contact', invoiceMattersClientParties, {
        showPartyNumber: true,
        useLinkWithOrigin: true,
    });
    return (
        <div className="flex flex-col">
            <div className="text-body1">{clientLinks}</div>
            {hasDifferingInvoiceClient && (
                <div className="text-body1">
                    <FormattedMessage
                        id="invoices.payer"
                        values={{
                            client: (
                                <PartyName
                                    key="client"
                                    party={clientParty}
                                    isLink
                                    useLinkWithOrigin
                                    variant="readonly-other"
                                />
                            ),
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export const InvoiceClient = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    const { invoiceMattersClientParties } = invoice;
    const clientLinks = getLinks('contact', invoiceMattersClientParties, {
        showPartyNumber: true,
        useLinkWithOrigin: true,
    });
    return (
        <div className="flex flex-col">
            <div className="text-body1">{clientLinks}</div>
        </div>
    );
};

export const InvoiceCreditInvoices = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    const { creditInvoices } = invoice;
    const links = getLinks(
        'invoice',
        creditInvoices?.map((el) => ({ id: el.id, name: el.invoiceNumber }))
    );
    return (
        <div className="flex flex-col">
            <div className="text-body1">{links}</div>
        </div>
    );
};

export const InvoiceRelationshipPartner = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    const { supervisingPartners, responsibleLawyers, matters } = invoice;

    const isJointInvoice = matters.length > 1;
    const allSupervisingPartners = supervisingPartners || [];
    const allResponsibleLawyers = responsibleLawyers || [];

    const hasNoResponsiblePersons = allSupervisingPartners.length === 0 && allResponsibleLawyers.length === 0;

    if (hasNoResponsiblePersons) {
        return <div className="flex flex-col">-</div>;
    }

    if (isJointInvoice) {
        return (
            <SupervisedAndResponsibleEmployees
                supervisedByEmployees={allSupervisingPartners}
                responsibleEmployees={allResponsibleLawyers}
                maxLineCount={2}
            />
        );
    } else {
        return (
            <SupervisedAndResponsibleEmployees
                supervisedByEmployees={allSupervisingPartners}
                responsibleEmployees={allResponsibleLawyers}
            />
        );
    }
};

export const InvoiceHours = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    const { billedTimeInHours } = invoice;
    return (
        <div className="flex flex-col">
            <HoursDisplay value={billedTimeInHours} />
        </div>
    );
};

export const InvoiceDate = ({
    invoice,
    classifiers,
    showStatus = false,
}: IgnoredTheme & {
    invoice: InvoiceDataListItem;
    classifiers: Record<string, ClassifierData>;
    showStatus?: boolean;
}): JSX.Element => {
    const isNewDesign = useIsNewDesign();
    const { invoiceTypeCode, invoiceDate, invoiceStatusTypeCode, isSplit, matters } = invoice;
    const invoiceTypeCodeClassifierValue = getClassifierText(classifiers['INVOICE_TYPE'], invoiceTypeCode);
    if (isNewDesign) {
        // The date, then the status and the type on the cell's bottom line.
        return (
            <div className="flex flex-col gap-1">
                {invoiceDate && <DateDefaultNowrap dateValue={invoiceDate} />}
                <div className="flex flex-wrap items-center gap-1">
                    {showStatus && (
                        <StatusChip
                            type={getInvoiceStatusChipType(invoiceStatusTypeCode)}
                            label={getClassifierText(classifiers['INVOICE_STATUS_TYPE'], invoiceStatusTypeCode)}
                        />
                    )}
                    <InvoiceIconGroup
                        typeCode={invoiceTypeCode}
                        typeLabel={invoiceTypeCodeClassifierValue}
                        isJointInvoice={matters.length > 1}
                        isSplit={isSplit ?? false}
                        row
                    />
                </div>
            </div>
        );
    }
    return (
        <div className="flex flex-row items-start">
            <InvoiceIconGroup
                typeCode={invoiceTypeCode}
                typeLabel={invoiceTypeCodeClassifierValue}
                isJointInvoice={matters.length > 1}
                isSplit={isSplit ?? false}
            />
            <div className="flex flex-col">
                {invoiceDate && <DateDefaultNowrap dateValue={invoiceDate} />}
                {showStatus && (
                    <div className="mt-1 flex flex-nowrap">
                        <StatusChip
                            type={getInvoiceStatusChipType(invoiceStatusTypeCode)}
                            label={getClassifierText(classifiers['INVOICE_STATUS_TYPE'], invoiceStatusTypeCode)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const isCorrection = (invoice: InvoiceDataListItem): boolean => invoice.invoiceTypeCode === InvoiceType.CORRECTION;

const getDisplayTotals = (invoice: InvoiceDataListItem): InvoiceListItemTotalsDto | undefined =>
    isCorrection(invoice) && invoice.invoiceDeltaTotals ? invoice.invoiceDeltaTotals : invoice.invoiceTotals;

const NotApplicableCell = (): JSX.Element => <p className="text-body1">-</p>;

// Stacks the payment-currency line above the base-currency line. Two block lines only stack via the
// parent table cell's default box model, which the DataTable engine drops once virtualized rows
// switch cells to `display: flex` — an explicit column here keeps it stacked either way.
const PaymentAndBaseAmount = ({
    paymentAmount,
    paymentCurrency,
    baseAmount,
    baseCurrency,
}: {
    paymentAmount: number;
    paymentCurrency: string;
    baseAmount: number;
    baseCurrency: string;
}): JSX.Element => (
    <div className="flex flex-col">
        <p className="text-body1">
            <MoneyDisplay amount={paymentAmount} currency={paymentCurrency} />
        </p>
        <span className="text-caption text-fg-secondary">
            <MoneyDisplay amount={baseAmount} currency={baseCurrency} />
        </span>
    </div>
);

export const InvoiceVatTotal = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const vatTotal = invoiceTotals?.paymentCurrency?.invoice.vat ?? 0;
    const vatTotalBaseCurr = invoiceTotals?.baseCurrency?.invoice.vat ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={vatTotal}
            paymentCurrency={paymentCurrency}
            baseAmount={vatTotalBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const InvoiceBrutoTotal = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const totalWithVat = invoiceTotals?.paymentCurrency?.invoice.totalWithVat ?? 0;
    const totalWithVatBaseCurr = invoiceTotals?.baseCurrency?.invoice.totalWithVat ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={totalWithVat}
            paymentCurrency={paymentCurrency}
            baseAmount={totalWithVatBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const InvoicePrepaymentAmount = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element => {
    if (isCorrection(invoice)) {
        return <NotApplicableCell />;
    }
    const { invoiceTotals } = invoice;
    const paymentCurrency = invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const prepaymentAmount = invoiceTotals?.paymentCurrency?.invoice.prepaymentAmount ?? 0;
    const prepaymentAmountBaseCurr = invoiceTotals?.baseCurrency?.invoice.prepaymentAmount ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={prepaymentAmount * -1}
            paymentCurrency={paymentCurrency}
            baseAmount={prepaymentAmountBaseCurr * -1}
            baseCurrency={baseCurrency}
        />
    );
};

export const FeesWithDiscount = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element | null => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const feeTotal = invoiceTotals?.paymentCurrency?.fees.totalWithoutVat ?? 0;
    const feeTotalBaseCurr = invoiceTotals?.baseCurrency?.fees.totalWithoutVat ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={feeTotal}
            paymentCurrency={paymentCurrency}
            baseAmount={feeTotalBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const FeesWithoutDiscount = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element | null => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const feeTotal = invoiceTotals?.paymentCurrency?.fees.totalWithoutVat ?? 0;
    const feeDiscountAmount = invoiceTotals?.paymentCurrency?.fees.discountAdjustmentAmount ?? 0;
    const feeTotalBaseCurr = invoiceTotals?.baseCurrency?.fees.totalWithoutVat ?? 0;
    const feeDiscountAmountBaseCurr = invoiceTotals?.baseCurrency?.fees.discountAdjustmentAmount ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={feeTotal - feeDiscountAmount}
            paymentCurrency={paymentCurrency}
            baseAmount={feeTotalBaseCurr - feeDiscountAmountBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const VatAppliedExpensesWithoutDiscount = ({
    invoice,
}: {
    invoice: InvoiceDataListItem;
}): JSX.Element | null => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const expenseTotal = invoiceTotals?.paymentCurrency?.expensesWithVat.totalWithoutVat ?? 0;
    const expenseDiscountAmount = invoiceTotals?.paymentCurrency?.expensesWithVat.discountAdjustmentAmount ?? 0;
    const expenseTotalBaseCurr = invoiceTotals?.baseCurrency?.expensesWithVat.totalWithoutVat ?? 0;
    const expenseDiscountAmountBaseCurr = invoiceTotals?.baseCurrency?.expensesWithVat.discountAdjustmentAmount ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={expenseTotal - expenseDiscountAmount}
            paymentCurrency={paymentCurrency}
            baseAmount={expenseTotalBaseCurr - expenseDiscountAmountBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const NonVatExpensesWithoutDiscount = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element | null => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const expenseTotal = invoiceTotals?.paymentCurrency?.expensesWithoutVat.totalWithoutVat ?? 0;
    const expenseDiscountAmount = invoiceTotals?.paymentCurrency?.expensesWithoutVat.discountAdjustmentAmount ?? 0;
    const expenseTotalBaseCurr = invoiceTotals?.baseCurrency?.expensesWithoutVat.totalWithoutVat ?? 0;
    const expenseDiscountAmountBaseCurr = invoiceTotals?.baseCurrency?.expensesWithoutVat.discountAdjustmentAmount ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={expenseTotal - expenseDiscountAmount}
            paymentCurrency={paymentCurrency}
            baseAmount={expenseTotalBaseCurr - expenseDiscountAmountBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const InvoiceStatus = ({
    invoice,
    classifiers,
}: IgnoredTheme & {
    invoice: InvoiceDataListItem;
    classifiers: Record<string, ClassifierData>;
}): JSX.Element => {
    const { invoiceStatusTypeCode } = invoice;
    return (
        <div className="flex flex-nowrap">
            <StatusChip
                type={getInvoiceStatusChipType(invoiceStatusTypeCode)}
                label={getClassifierText(classifiers['INVOICE_STATUS_TYPE'], invoiceStatusTypeCode)}
            />
        </div>
    );
};

export const InvoiceStatusChange = ({
    invoice,
    classifiers,
    contactId,
    matterId,
    chips,
}: IgnoredTheme & {
    invoice: InvoiceDataListItem;
    classifiers: Record<string, ClassifierData>;
    contactId?: string;
    matterId?: string;
    /** More chips for the status line, which then wraps: the new design keeps a cell's chips on one line. */
    chips?: React.ReactNode;
}): JSX.Element => {
    const { id, invoiceStatusTypeCode, statusChangeDate } = invoice;
    return (
        <div className="flex flex-col">
            {statusChangeDate ? (
                <LinkWithOrigin className={ENTITY_LINK} to={invoicePath(id, { contactId, matterId })}>
                    <DateDefaultNowrap dateValue={statusChangeDate} />
                </LinkWithOrigin>
            ) : null}
            <div className={chips ? 'flex flex-wrap items-center gap-1' : 'flex flex-nowrap'}>
                <StatusChip
                    type={getInvoiceStatusChipType(invoiceStatusTypeCode)}
                    label={getClassifierText(classifiers['INVOICE_STATUS_TYPE'], invoiceStatusTypeCode)}
                />
                {chips}
            </div>
        </div>
    );
};

export const InvoiceNumber = ({
    invoice,
    contactId,
    matterId,
    target,
}: {
    invoice: InvoiceDataListItem;
    contactId?: string;
    matterId?: string;
    target?: HTMLAttributeAnchorTarget;
}): JSX.Element => {
    const { id, invoiceNumber } = invoice;
    return (
        <LinkWithOrigin
            className={cn(ENTITY_LINK, 'font-semibold')}
            to={invoicePath(id, { contactId, matterId })}
            target={target}
        >
            <Unmasked as="div">
                <div>{invoiceNumber}</div>
            </Unmasked>
        </LinkWithOrigin>
    );
};

export const InvoiceFeesTotal = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element | null => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const feeAmount = invoiceTotals?.paymentCurrency?.fees.totalWithoutVat ?? 0;
    const feeAmountBaseCurr = invoiceTotals?.baseCurrency?.fees.totalWithoutVat ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={feeAmount}
            paymentCurrency={paymentCurrency}
            baseAmount={feeAmountBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const InvoiceExpensesTotal = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element | null => {
    const invoiceTotals = getDisplayTotals(invoice);
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const expenseAmount =
        (invoiceTotals?.paymentCurrency?.expensesWithVat.totalWithoutVat ?? 0) +
        (invoiceTotals?.paymentCurrency?.expensesWithoutVat.totalWithoutVat ?? 0);
    const expenseAmountBaseCurr =
        (invoiceTotals?.baseCurrency?.expensesWithVat.totalWithoutVat ?? 0) +
        (invoiceTotals?.baseCurrency?.expensesWithoutVat.totalWithoutVat ?? 0);

    return (
        <PaymentAndBaseAmount
            paymentAmount={expenseAmount}
            paymentCurrency={paymentCurrency}
            baseAmount={expenseAmountBaseCurr}
            baseCurrency={baseCurrency}
        />
    );
};

export const InvoiceAmountDisplay = ({
    invoice,
    fieldName,
}: {
    invoice: InvoiceDataListItem;
    fieldName: keyof InvoiceAmountDetailsTotalsDto['invoice'];
}): JSX.Element | null => {
    const invoiceTotals = getDisplayTotals(invoice);
    // For corrections we read from delta_amounts_json, where only vat/totalWithVat/totalWithoutVat
    // are reliably populated (legacy rows have stale or missing amountDue). Since corrections never
    // have prepayment, totalWithVat is the right substitute for amountDue.
    const effectiveField: keyof InvoiceAmountDetailsTotalsDto['invoice'] =
        isCorrection(invoice) && fieldName === 'amountDue' ? 'totalWithVat' : fieldName;
    const paymentCurrency = invoice.invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoice.invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';
    const paymentAmount = invoiceTotals?.paymentCurrency?.invoice[effectiveField] ?? 0;
    const baseAmount = invoiceTotals?.baseCurrency?.invoice[effectiveField] ?? 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={paymentAmount}
            paymentCurrency={paymentCurrency}
            baseAmount={baseAmount}
            baseCurrency={baseCurrency}
        />
    );
};

export const InvoiceOutstandingBalance = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element | null => {
    if (isCorrection(invoice)) {
        return <NotApplicableCell />;
    }
    const { invoiceTotals } = invoice;
    const paymentCurrency = invoiceTotals?.paymentCurrency?.currencyTypeCode ?? '';
    const baseCurrency = invoiceTotals?.baseCurrency?.currencyTypeCode ?? '';

    const paymentAmount = parseFloat(invoice.outstandingBalanceInPaymentCurrency || '0') || 0;
    const baseAmount = parseFloat(invoice.outstandingBalanceInBaseCurrency || '0') || 0;

    return (
        <PaymentAndBaseAmount
            paymentAmount={paymentAmount}
            paymentCurrency={paymentCurrency}
            baseAmount={baseAmount}
            baseCurrency={baseCurrency}
        />
    );
};

export const InvoiceDeadline = ({ invoice }: IgnoredTheme & { invoice: InvoiceDataListItem }): JSX.Element => {
    const { clientParty, invoiceTypeCode, invoiceStatusTypeCode } = invoice;

    const allowedStatuses = ['DRAFT', 'REVIEWED', 'APPROVED', 'FINALIZED'];
    if (
        invoiceTypeCode !== 'DEBIT' ||
        !allowedStatuses.includes(invoiceStatusTypeCode) ||
        !clientParty.invoicingDeadlineDay
    ) {
        return <p className="text-body1">-</p>;
    }

    const today = new Date();
    const deadlineDay = clientParty.invoicingDeadlineDay;

    // magic js line to get the last day of the month accounting for leap years
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const actualDeadlineDay = Math.min(deadlineDay, lastDayOfMonth);

    const deadlineDate = new Date(today.getFullYear(), today.getMonth(), actualDeadlineDay);

    const timeDiff = deadlineDate.getTime() - today.getTime();
    const daysDifference = Math.ceil(timeDiff / (1000 * 3600 * 24));

    const shouldHighlight = daysDifference <= 0;
    const text = `${daysDifference}d`;

    return <p className={cn('text-body1', shouldHighlight && 'text-fg-error')}>{text}</p>;
};

export const SentPdfCell = ({ invoice }: { invoice: InvoiceDataListItem }): JSX.Element | null => {
    const { latestSentInvoiceId } = invoice;
    if (!latestSentInvoiceId) return null;
    return <PdfLink sentInvoiceId={latestSentInvoiceId} />;
};
